import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

export function useDeepLinks() {
  const navigate = useNavigate();
  const REDIRECT_URL = 'https://ios.mindnote.online';

  const handleVerificationRedirect = async () => {
    console.log('App: Initiating verification redirect...');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (Capacitor.isNativePlatform()) {
        await Browser.open({
          url: REDIRECT_URL,
          windowName: '_self'
        });
        console.log('App: Successfully opened app URL in same tab');
      } else {
        window.location.href = REDIRECT_URL;
      }
    } catch (error) {
      console.error('App: Error opening app URL:', error);
      try {
        window.location.href = REDIRECT_URL;
      } catch (secondError) {
        console.error('App: Fallback redirect failed:', secondError);
      }
    }
  };

  useEffect(() => {
    // Handle deep links in native platforms
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appUrlOpen', async ({ url }) => {
        console.log('Deep link URL received:', url);

        try {
          if (url.includes('verify') || url.includes('email-verified')) {
            console.log('Verification URL detected in deep link');
            
            // Set verifying flag to show loading UI
            localStorage.setItem('emailVerifying', 'true');
            
            // Trigger a storage event to update UI immediately
            window.dispatchEvent(new Event('storage'));

            // Parse tokens from both hash and query params (EmailVerified page sends them as query params)
            const parsedUrl = new URL(url);
            const hashParams = new URLSearchParams(url.split('#')[1]);
            const accessToken = hashParams.get('access_token') || parsedUrl.searchParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token') || parsedUrl.searchParams.get('refresh_token');
            const type = hashParams.get('type') || parsedUrl.searchParams.get('type');

            if (accessToken && (type === 'signup' || type === 'session')) {
              try {
                const { error } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken || '',
                });
                if (error) {
                  console.error('Error setting session:', error);
                  localStorage.removeItem('emailVerifying');
                  return;
                }
                
                // Successfully verified - navigate to auth page to show overlay
                console.log('Email verified successfully, navigating to auth page...');
                navigate('/auth');
              } catch (sessionError) {
                console.error('Session error:', sessionError);
                localStorage.removeItem('emailVerifying');
              }
            }
          }
        } catch (error) {
          console.error('Error handling deep link:', error);
          localStorage.removeItem('emailVerifying');
        }
      });
    }

    return () => {
      if (Capacitor.isNativePlatform()) {
        CapApp.removeAllListeners();
      }
    };
  }, [navigate]);

  // This hook mostly has side effects, but we can expose the redirect handler for use elsewhere
  return { handleVerificationRedirect };
}
